export interface BakingSession {
  id: string;
  userId: string;
  name: string;
  targetBakeTime: Date;
  fermentationType: FermentationType;
  ovenType: 'domestic' | 'wood-fired';
  woodOvenDetails?: {
    size: string;
    lastUsed: string;
  };
  customBulkTime?: number;
  customProofTime?: number;
  starterId: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  starterSchedule?: {
    feeds: Array<{
      id: string;
      scheduledTime: Date;
      completed: boolean;
      noFeedsNeeded?: boolean;
    }>;
    customized: boolean;
  };
  result?: {
    imageUrl: string;
    thumbnailUrl?: string;
    comments?: string;
    completedAt: Date;
  };
}

export type FermentationType = 'direct' | 'cold-bulk' | 'slow-room' | 'pre-proofed';
export type StarterTexture = 'liquid' | 'doughy' | 'hard';
export type StarterStorage = 'room' | 'fridge';
export type StarterManagementStrategy = 'relax' | 'normal' | 'champion' | 'overboost';

export const TEXTURE_RATIOS: Record<StarterTexture, FeedingRatio[]> = {
  liquid: ['1-1-1', '1-3-3', '1-5-5'],
  doughy: ['1-1-0.7', '1-2-1.4', '1-5-3.5'],
  hard: ['1-1-0.5', '1-2-1', '1-6-3']
};

export interface Starter {
  id: string;
  userId: string;
  name: string;
  texture: StarterTexture;
  feedingRatio: FeedingRatio;
  flourType: string;
  storage: StarterStorage;
  roomTemperature?: number; // Added field for room temperature in °C
  lastFed: Date;
  createdAt: Date;
  isFavorite: boolean;
  managementStrategy: StarterManagementStrategy;
}

export interface FermentationProcess {
  id: FermentationType;
  name: string;
  totalTime: number;
  bulkTime: number;
  proofTime: number;
  temperature: 'room' | 'cold';
  starterPercentage: string;
  description: string;
}

export const FERMENTATION_PROCESSES: FermentationProcess[] = [
  {
    id: 'direct',
    name: 'Direct Fermentation',
    totalTime: 6,
    bulkTime: 4,
    proofTime: 2,
    temperature: 'room',
    starterPercentage: '20-30%',
    description: 'Quick process at room temperature, ideal for same-day baking'
  },
  {
    id: 'cold-bulk',
    name: 'Slow Fermentation (Cold)',
    totalTime: 18,
    bulkTime: 16,
    proofTime: 2,
    temperature: 'cold',
    starterPercentage: '20-30%',
    description: 'Long bulk fermentation in fridge for enhanced flavor'
  },
  {
    id: 'slow-room',
    name: 'Slow Fermentation (Room)',
    totalTime: 18,
    bulkTime: 16,
    proofTime: 2,
    temperature: 'room',
    starterPercentage: '0.5-1%',
    description: 'Extended room temperature fermentation with minimal starter'
  },
  {
    id: 'pre-proofed',
    name: 'Cold Retard',
    totalTime: 18,
    bulkTime: 3,
    proofTime: 15,
    temperature: 'cold',
    starterPercentage: '20-30%',
    description: 'Flexible process with cold proofing up to 120 hours'
  }
];

export const STARTER_TEXTURES = [
  { id: 'liquid', label: 'Liquid' },
  { id: 'doughy', label: 'Doughy' },
  { id: 'hard', label: 'Stiff' }
];


export interface ManagementStrategy {
  id: StarterManagementStrategy;
  label: string;
  description: string;
  feedingInterval: {
    room: number; // Base hours at normal room temperature (20-25°C)
    fridge: number;
  };
  allowFridge: boolean;
}

export const MANAGEMENT_STRATEGIES: Record<StarterManagementStrategy, ManagementStrategy> = {
  relax: {
    id: 'relax',
    label: 'Relax Mode',
    description: 'Longer storage time between feeds',
    feedingInterval: {
      room: 72, // 3 days in hours
      fridge: 360 // 15 days in hours
    },
    allowFridge: true
  },
  normal: {
    id: 'normal',
    label: 'Normal Mode',
    description: 'Standard feeding schedule',
    feedingInterval: {
      room: 48, // 2 days in hours
      fridge: 240 // 10 days in hours
    },
    allowFridge: true
  },
  champion: {
    id: 'champion',
    label: 'Champion Mode',
    description: 'For optimum bread results',
    feedingInterval: {
      room: 24, // 1 day in hours
      fridge: 168 // 7 days in hours
    },
    allowFridge: true
  },
  overboost: {
    id: 'overboost',
    label: 'Overboost Mode',
    description: 'Intensive care for weak starters',
    feedingInterval: {
      room: 12, // 12 hours
      fridge: 12 // Not recommended, but kept for consistency
    },
    allowFridge: false
  }
} as const;